import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

import { useImageZoom } from '@/hooks/useImageZoom';
import { useLightboxNavigation } from '@/hooks/useLightboxNavigation';
import { useSwipeNavigation } from '@/hooks/useSwipeNavigation';

import { LightboxStage } from './LightboxStage';
import { LightboxThumbRail } from './LightboxThumbRail';
import { type ViewerImage } from './types';
import { ViewerCaption } from './ViewerCaption';
import { ViewerIconButton } from './ViewerIconButton';
import { ViewerShell } from './ViewerShell';

type Props = {
  open: boolean;
  images: readonly ViewerImage[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  /** The dialog's accessible name. */
  label?: string;
};

/**
 * Full-screen photographs, paged.
 *
 * Reusable on purpose, and the reason this folder exists: the vehicle detail
 * page has had a private copy of roughly this component since it was written
 * (`pages/vehicle/organisms/VehicleGallery`), a 250-line organism that is a
 * carousel, a thumbnail strip, a video list and a dialog at once. Everything
 * general about it is here, split so that each part is answerable on its own —
 * the stage draws one picture, the rail picks between them, the caption says
 * what they are, and this file wires those three to the keyboard and the
 * thumb. The page keeps only its own view model.
 *
 * ## Why the arrows are not in the shell
 *
 * The reel has no arrows: it scrolls. The counter and the close button are
 * shared chrome; paging controls belong to the viewer that pages.
 *
 * ## Zoom suspends paging
 *
 * A swipe is "next photograph" at fit and "pan" when zoomed in, and the same
 * gesture cannot mean both. Zooming therefore stands the swipe handler and the
 * arrow keys down, and paging resets the zoom on the way out — so arriving at
 * the next photograph already enlarged and off-centre cannot happen.
 */
export function MediaLightbox({
  open,
  images,
  index,
  onIndexChange,
  onClose,
  label = 'Photographs',
}: Props) {
  const zoom = useImageZoom(index);
  const navigation = useLightboxNavigation({
    count: images.length,
    index,
    onIndexChange,
    disabled: zoom.zoomed,
    enabled: open,
  });
  const swipe = useSwipeNavigation({
    onNext: navigation.next,
    onPrevious: navigation.previous,
    disabled: zoom.zoomed,
  });

  const current = images[navigation.index];
  const paged = images.length > 1;

  return (
    <ViewerShell
      open={open}
      onClose={onClose}
      label={label}
      position={images.length > 1 ? navigation.position : undefined}
      onKeyDown={navigation.onKeyDown}
    >
      {current && (
        <Stack sx={{ height: '100svh', pt: { xs: 14, md: 16 } }}>
          <LightboxStage image={current} zoom={zoom} swipe={swipe} />

          {paged && !zoom.zoomed && (
            <>
              {/* Positioned against the dialog rather than the stage, so they
                  stay put as photographs of different shapes come and go. */}
              <Box
                sx={{
                  position: 'absolute',
                  left: { xs: 8, md: 20 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                }}
              >
                <ViewerIconButton
                  label="Previous photograph"
                  icon={<ChevronLeft />}
                  size={56}
                  onClick={navigation.previous}
                />
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  right: { xs: 8, md: 20 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                }}
              >
                <ViewerIconButton
                  label="Next photograph"
                  icon={<ChevronRight />}
                  size={56}
                  onClick={navigation.next}
                />
              </Box>
            </>
          )}

          {/* The foot: what this is, then what else there is. Both are hidden
              while zoomed — somebody reading a number plate wants the picture
              and not the furniture. */}
          {!zoom.zoomed && (
            <Stack spacing={5} sx={{ flexShrink: 0, pt: { xs: 4, md: 5 }, pb: { xs: 3, md: 4 } }}>
              {current.caption && (
                <Box sx={{ px: { xs: 3, md: 5 } }}>
                  <ViewerCaption caption={current.caption} variant="panel" />
                </Box>
              )}
              <LightboxThumbRail
                images={images}
                index={navigation.index}
                onSelect={onIndexChange}
              />
            </Stack>
          )}
        </Stack>
      )}
    </ViewerShell>
  );
}
